import SearchableSelect from '../components/search/SearchableSelect';
import CollectionSearch from '../components/search/CollectionSearch';
import { filterRecords } from '../utils/search';
import React, { useState, useEffect } from 'react'; // Ensure all are imported
import {
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Grid,
  Stack,
  Container, Typography, Box, Button, CircularProgress, Alert, List, ListItem, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getSplitTemplates, createSplitTemplate, updateSplitTemplate, deleteSplitTemplate, getCategories, getAccounts } from '../services/api';

function SplitTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]); // New state for accounts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(''); // New state for account selection
  const [defaultAmount, setDefaultAmount] = useState('');
  const [falloverCategoryId, setFalloverCategoryId] = useState('');
  const [allocations, setAllocations] = useState([]);
  const [transactionType, setTransactionType] = useState('income');
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchTemplatesAndCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const templatesRes = await getSplitTemplates();
      setTemplates(templatesRes.data);
      const categoriesRes = await getCategories();
      setCategories(categoriesRes.data);
      const accountsRes = await getAccounts();
      setAccounts(accountsRes.data);
    } catch (err) {
      console.error("Failed to fetch data", err);
      setError(err.response?.data?.msg || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplatesAndCategories();
  }, []);

  const handleOpenAddModal = () => {
    setCurrentTemplate(null);
    setTemplateName('');
    setSelectedAccount(''); // Reset selected account
    setDefaultAmount('');
    setFalloverCategoryId('');
    setTransactionType('income');
    setAllocations([]);
    setOpenAddEditModal(true);
  };

  const handleOpenEditModal = (template) => {
    setCurrentTemplate(template);
    setTemplateName(template.name);
    setSelectedAccount(template.account_id); // Set selected account for editing
    setDefaultAmount(template.default_amount || '');
    setFalloverCategoryId(template.fallover_category_id || '');
    setTransactionType(template.transaction_type || 'income');
    setAllocations(template.allocations.map(alloc => ({ ...alloc, value: parseFloat(alloc.value) })));
    setOpenAddEditModal(true);
  };

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    setCurrentTemplate(null);
  };

  const handleAddAllocation = () => {
    setAllocations([...allocations, { category_id: '', allocation_type: 'percentage', value: '' }]);
  };

  const handleAllocationChange = (index, field, value) => {
    const newAllocations = [...allocations];
    newAllocations[index][field] = value;
    setAllocations(newAllocations);
  };

  const handleRemoveAllocation = (index) => {
    const newAllocations = [...allocations];
    newAllocations.splice(index, 1);
    setAllocations(newAllocations);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const templateData = {
        name: templateName,
        account_id: selectedAccount,
        default_amount: (transactionType === 'income' && defaultAmount) ? parseFloat(defaultAmount) : null,
        fallover_category_id: falloverCategoryId || null,
        transaction_type: transactionType,
        allocations: allocations.map(alloc => ({ ...alloc, value: parseFloat(alloc.value) }))
      };

      if (currentTemplate) {
        await updateSplitTemplate(currentTemplate.id, templateData);
      } else {
        await createSplitTemplate(templateData);
      }
      fetchTemplatesAndCategories();
      handleCloseAddEditModal();
    } catch (err) {
      console.error("Failed to save template", err);
      setError(err.response?.data?.msg || 'Failed to save template');
    }
  };

  const handleOpenConfirmDelete = (template) => {
    setTemplateToDelete(template);
    setOpenConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
    setTemplateToDelete(null);
  };

  const handleConfirmDelete = async () => {
    setError('');
    try {
      await deleteSplitTemplate(templateToDelete.id);
      fetchTemplatesAndCategories();
      handleCloseConfirmDelete();
    } catch (err) {
      console.error("Failed to delete template", err);
      setError(err.response?.data?.msg || 'Failed to delete template');
    }
  };

  const visibleTemplates = filterRecords(templates, search, template => [
    template.name, accounts.find(account => account.id === template.account_id)?.name,
    template.transaction_type, template.fallover_category_name, template.default_amount,
    (template.allocations || []).map(allocation => allocation.category_name),
  ], { rank: true });

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Split Templates
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={handleOpenAddModal} 
          sx={{ 
            mb: 2,
            width: { xs: '100%', sm: 'auto' },
            py: { xs: 1.5, md: 1 }
          }}
        >
          Create New Template
        </Button>

        <CollectionSearch label="Search split templates" value={search} onChange={setSearch}
          total={templates.length} shown={visibleTemplates.length} />
        {search && !visibleTemplates.length && <Typography>No matching split templates.</Typography>}
        {templates.length > 0 ? (
  <Grid container spacing={2}>
    {visibleTemplates.map((template) => (
      <Grid item xs={12} md={6} key={template.id}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {template.name}
            </Typography>
            
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={template.transaction_type === 'income' ? '💰 Income' : '💸 Expense'}
                  color={template.transaction_type === 'income' ? 'success' : 'error'}
                  size="small"
                />
                {template.is_scenario && (
                  <Chip
                    label="🧪 Scenario — excluded from projections"
                    color="warning"
                    size="small"
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {accounts.find(acc => acc.id === template.account_id)?.name || 'N/A'}
                </Typography>
              </Box>
              
              {template.transaction_type === 'income' && (
                <Typography variant="body2">
                  Default: ${template.default_amount || 'N/A'}
                </Typography>
              )}
              
              <Typography variant="body2">
                Fallover: {template.fallover_category_name || 'None'}
              </Typography>
              
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Allocations:
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {template.allocations.map(alloc => (
                    <Typography key={alloc.id} variant="caption" sx={{ ml: 1 }}>
                      • {alloc.category_name}: {alloc.value}{alloc.allocation_type === 'percentage' ? '%' : ''}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
          
          <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
            <IconButton 
              aria-label="edit" 
              onClick={() => handleOpenEditModal(template)}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              aria-label="delete" 
              onClick={() => handleOpenConfirmDelete(template)}
              sx={{ minWidth: 44, minHeight: 44 }}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        </Card>
      </Grid>
    ))}
  </Grid>
) : (
  <Card>
    <CardContent>
      <Typography align="center" color="text.secondary">
        No split templates created yet.
      </Typography>
    </CardContent>
  </Card>
)}

        <Dialog 
  open={openAddEditModal} 
  onClose={handleCloseAddEditModal}
  maxWidth="md"
  fullWidth
  sx={{
    '& .MuiDialog-paper': {
      width: { xs: '95%', sm: '90%', md: '80%' },
      maxHeight: { xs: '90vh', md: '80vh' },
      m: { xs: 1, sm: 2 }
    }
  }}
>
          <DialogTitle>{currentTemplate ? 'Edit Split Template' : 'Create New Split Template'}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              autoFocus
              margin="dense"
              label="Template Name"
              type="text"
              fullWidth
              variant="standard"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
            <FormControl fullWidth margin="dense" variant="standard" required>
              <InputLabel>Template Type</InputLabel>
              <Select
                value={transactionType}
                onChange={(e) => {
                  setTransactionType(e.target.value);
                  if (e.target.value === 'expense') {
                    setDefaultAmount(''); // Clear default amount for expense templates
                  }
                }}
                label="Template Type"
              >
                <MenuItem value="income">Income Distribution</MenuItem>
                <MenuItem value="expense">Expense Allocation</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense" variant="standard" required>
              <InputLabel>Account</InputLabel>
              <SearchableSelect
                value={selectedAccount}
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  setFalloverCategoryId(''); // Reset fallover category when account changes
                  setAllocations([]); // Reset allocations when account changes
                }}
                label="Account"
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <TextField
              margin="dense"
              label={transactionType === 'income' ? "Default Amount (optional)" : "Amount (entered per transaction)"}
              type="number"
              fullWidth
              variant="standard"
              value={transactionType === 'income' ? defaultAmount : ''}
              onChange={(e) => setDefaultAmount(e.target.value)}
              disabled={transactionType === 'expense'}
              helperText={transactionType === 'expense' ? 
                "Expense amounts are entered when creating transactions" : 
                "Set a typical income amount or leave blank"}
            />
            <FormControl fullWidth margin="dense" variant="standard">
              <InputLabel>Fallover Category (optional)</InputLabel>
              <SearchableSelect
                value={falloverCategoryId}
                onChange={(e) => setFalloverCategoryId(e.target.value)}
                label="Fallover Category"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {categories.filter(cat => cat.account_id === selectedAccount).map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>

            <Typography variant="h6" sx={{ mt: 2 }}>Allocations</Typography>
            {allocations.map((alloc, index) => (
  <Box 
    key={index} 
    sx={{ 
      display: 'flex', 
      gap: 1, 
      mb: 1,
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'center' }
    }}
  >
    <FormControl 
      size="small" 
      sx={{ 
        minWidth: { xs: '100%', sm: 120 },
        flex: { xs: '1 1 auto', sm: '0 0 auto' }
      }}
    >
      <InputLabel>Category</InputLabel>
      <SearchableSelect
        value={alloc.category_id}
        onChange={(e) => handleAllocationChange(index, 'category_id', e.target.value)}
        label="Category"
      >
        {categories.filter(cat => cat.account_id === selectedAccount).map((category) => (
          <MenuItem key={category.id} value={category.id}>
            {category.name}
          </MenuItem>
        ))}
      </SearchableSelect>
    </FormControl>
    
    <FormControl 
      size="small" 
      sx={{ 
        minWidth: { xs: '100%', sm: 100 },
        flex: { xs: '1 1 auto', sm: '0 0 auto' }
      }}
    >
      <InputLabel>Type</InputLabel>
      <Select
        value={alloc.allocation_type}
        onChange={(e) => handleAllocationChange(index, 'allocation_type', e.target.value)}
        label="Type"
      >
        <MenuItem value="percentage">Percentage</MenuItem>
        <MenuItem value="fixed">Fixed</MenuItem>
      </Select>
    </FormControl>
    
    <TextField
      label="Value"
      type="number"
      size="small"
      value={alloc.value}
      onChange={(e) => handleAllocationChange(index, 'value', e.target.value)}
      sx={{ 
        flex: { xs: '1 1 auto', sm: '0 0 80px' },
        '& .MuiInputBase-input': { 
          fontSize: { xs: '16px', md: '14px' }
        }
      }}
    />
    
    <IconButton 
      color="error" 
      onClick={() => handleRemoveAllocation(index)}
      sx={{ 
        minWidth: 44, 
        minHeight: 44,
        alignSelf: { xs: 'center', sm: 'auto' }
      }}
    >
      <DeleteIcon />
    </IconButton>
  </Box>
))}
            <Button startIcon={<AddIcon />} onClick={handleAddAllocation} sx={{ mt: 2 }}>
              Add Allocation
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEditModal}>Cancel</Button>
            <Button onClick={handleSubmit}>{currentTemplate ? 'Update' : 'Create'}</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openConfirmDelete}
          onClose={handleCloseConfirmDelete}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Confirm Deletion"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete template "{templateToDelete?.name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDelete}>Cancel</Button>
            <Button onClick={handleConfirmDelete} autoFocus color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default SplitTemplatesPage;
